import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { Clipboard } from "@capacitor/clipboard";
import { Capacitor } from "@capacitor/core";
import { Share } from "@capacitor/share";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { toastrSuccess, toastrError } from "../components/toastr";

const isNative = Capacitor.isNativePlatform();

const FEATURES = [
  {
    emoji: "📋",
    title: "Live queue tracking",
    desc: "Add customers and track their wait in real-time.",
  },
  {
    emoji: "🍽️",
    title: "Menu on public page",
    desc: "Customers see your menu while they wait.",
  },
  {
    emoji: "🪑",
    title: "Table assignment",
    desc: "Seat customers to a specific table on check-in.",
  },
  {
    emoji: "👥",
    title: "Staff PIN access",
    desc: "Each waiter logs in with their own 6-digit PIN.",
  },
];

// ── Progress dots (capsule style) ─────────────────────────────────────────────
function StepDots({ step }) {
  return (
    <div className="flex items-center gap-2 justify-center">
      {[1, 2].map((s, i) => (
        <React.Fragment key={s}>
          <div
            className={`rounded-full transition-all duration-300 ${
              s === step
                ? "w-5 h-2 bg-primary"
                : s < step
                ? "w-2 h-2 bg-primary/40"
                : "w-2 h-2 bg-gray-200"
            }`}
          />
          {i < 1 && (
            <div
              className={`w-6 h-0.5 ${step > 1 ? "bg-primary/40" : "bg-gray-200"}`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Screen 1: QR Code ─────────────────────────────────────────────────────────
function QRScreen({ publicUrl, qrCanvasRef, onCopy, onShareOrDownload }) {
  return (
    <div className="flex-1 min-h-0 overflow-y-auto px-5 py-2">
      <div className="w-full max-w-sm mx-auto flex flex-col gap-3">

        {/* QR card */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-4">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">
            📱 Your public queue link
          </p>
          <div className="flex items-start gap-3">
            {/* QR code */}
            <div className="flex-shrink-0 bg-gray-50 rounded-xl p-2 border border-gray-100">
              {publicUrl ? (
                <QRCodeCanvas
                  ref={qrCanvasRef}
                  value={publicUrl}
                  size={110}
                  bgColor="#f9fafb"
                  fgColor="#111827"
                  level="M"
                />
              ) : (
                <div className="w-[110px] h-[110px] bg-gray-100 rounded-lg" />
              )}
            </div>
            {/* Info */}
            <div className="flex flex-col gap-2 min-w-0 pt-0.5">
              <p className="text-xs text-gray-600 leading-relaxed">
                Customers scan this to see their live position in your queue.{" "}
                <span className="font-semibold text-gray-800">No app needed.</span>
              </p>
              {publicUrl && (
                <div className="bg-primary/5 rounded-lg px-2 py-1.5">
                  <p className="text-[10px] text-primary font-mono break-all leading-snug">
                    {publicUrl}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-2 mt-3">
            <button
              onClick={onCopy}
              className="flex-1 py-2.5 rounded-xl text-xs font-semibold border border-gray-200 text-gray-700 flex items-center justify-center gap-1.5 active:bg-gray-50"
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy Link
            </button>
            <button
              onClick={onShareOrDownload}
              className="flex-1 py-2.5 rounded-xl text-xs font-semibold bg-primary text-white flex items-center justify-center gap-1.5 active:opacity-90"
            >
              {isNative ? (
                <>
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  Share QR
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download QR
                </>
              )}
            </button>
          </div>
        </div>

        {/* How-to box */}
        <div className="bg-white border border-gray-100 rounded-2xl px-4 py-3 flex items-start gap-3">
          <span className="text-lg flex-shrink-0 mt-0.5">💡</span>
          <div>
            <p className="text-xs font-semibold text-gray-800 mb-1">How to use this</p>
            <p className="text-xs text-gray-500 leading-relaxed">
              Print this QR and place it at your entrance. When a customer
              joins your queue, they scan it to track their spot live — no
              crowding, no shouting names.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}

// ── Screen 2: Features ────────────────────────────────────────────────────────
function FeaturesScreen() {
  return (
    <div className="flex-1 min-h-0 overflow-y-auto px-5 py-2">
      <div className="w-full max-w-sm mx-auto flex flex-col gap-2.5">

        {FEATURES.map((f) => (
          <div
            key={f.title}
            className="bg-white rounded-2xl border border-gray-100 shadow-card px-4 py-3.5 flex items-center gap-3"
          >
            <div className="w-9 h-9 bg-primary/8 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-lg leading-none">{f.emoji}</span>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-800 leading-tight">{f.title}</p>
              <p className="text-xs text-gray-500 mt-0.5 leading-snug">{f.desc}</p>
            </div>
          </div>
        ))}

        {/* First step hint */}
        <div className="bg-amber-50 border border-amber-100 rounded-2xl px-4 py-3 flex items-start gap-2.5 mt-0.5">
          <span className="text-base flex-shrink-0 mt-0.5">👆</span>
          <div>
            <p className="text-xs font-semibold text-amber-900 mb-0.5">Your first step</p>
            <p className="text-xs text-amber-800 leading-relaxed">
              Go to <span className="font-semibold">Settings → Menu &amp; Public Page</span> to
              add your business name and logo so customers recognise your queue page.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
function Welcome() {
  const navigate = useNavigate();
  const qrCanvasRef = useRef(null);
  const [step, setStep] = useState(1);

  const ownerId = localStorage.getItem("ownerId");
  const publicUrl = ownerId
    ? `https://pazhanam.com/waitlessq/#/public/waitlist/${ownerId}`
    : null;

  useEffect(() => {
    if (localStorage.getItem("welcomeShown") === "true") {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate]);

  function handleDone() {
    localStorage.setItem("welcomeShown", "true");
    navigate("/dashboard");
  }

  async function handleCopy() {
    if (!publicUrl) return;
    try {
      if (isNative) {
        await Clipboard.write({ string: publicUrl });
      } else {
        await navigator.clipboard.writeText(publicUrl);
      }
      toastrSuccess("Link copied!");
    } catch {
      toastrError("Could not copy link");
    }
  }

  async function handleShareOrDownload() {
    if (!publicUrl) return;
    if (isNative) {
      try {
        const canvas = qrCanvasRef.current;
        const base64 = canvas
          ? canvas.toDataURL("image/png").replace("data:image/png;base64,", "")
          : null;
        if (base64) {
          const file = await Filesystem.writeFile({
            path: "waitlessq-qr.png",
            data: base64,
            directory: Directory.Cache,
          });
          await Share.share({
            title: "My WaitLessQ Queue",
            text: `Scan to join my queue: ${publicUrl}`,
            url: file.uri,
            dialogTitle: "Share your queue QR code",
          });
        } else {
          throw new Error("no canvas");
        }
      } catch {
        try {
          await Share.share({ title: "My WaitLessQ Queue", text: publicUrl });
        } catch {
          toastrError("Could not share");
        }
      }
    } else {
      const canvas = qrCanvasRef.current;
      if (!canvas) return;
      const link = document.createElement("a");
      link.download = "waitlessq-qr.png";
      link.href = canvas.toDataURL("image/png");
      link.click();
    }
  }

  const heading = step === 1 ? "Your queue is live 🎉" : "What WaitLessQ does";
  const subtitle =
    step === 1
      ? "Share this with customers — they track their spot, no app needed."
      : "Everything you need to manage your walk-in queue.";

  return (
    /*
      h-[100dvh]: fills the true visible viewport. Since /welcome now uses
      OnboardingOnly (no Layout/BottomNav), there's no fixed 64px nav eating
      into this height — the full screen is available.
    */
    <div className="h-[100dvh] flex flex-col bg-surface overflow-hidden">

      {/* ── Top: dots + heading ── */}
      <div className="flex-shrink-0 px-5 pt-8 pb-3">
        <StepDots step={step} />
        <div className="mt-4">
          <h1 className="text-xl font-bold text-gray-900">{heading}</h1>
          <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
        </div>
      </div>

      {/* ── Step content (scrollable) ── */}
      {step === 1 ? (
        <QRScreen
          publicUrl={publicUrl}
          qrCanvasRef={qrCanvasRef}
          onCopy={handleCopy}
          onShareOrDownload={handleShareOrDownload}
        />
      ) : (
        <FeaturesScreen />
      )}

      {/* ── Sticky CTA — always visible, no BottomNav competing ── */}
      <div className="flex-shrink-0 px-5 pb-8 pt-3 bg-surface shadow-[0_-4px_16px_rgba(0,0,0,0.06)]">
        <div className="w-full max-w-sm mx-auto">
          {step === 1 ? (
            <>
              <button
                onClick={() => setStep(2)}
                className="w-full py-3.5 bg-primary text-white font-bold text-sm rounded-2xl active:opacity-90"
              >
                Next: What WaitLessQ does →
              </button>
              <p className="text-xs text-gray-400 text-center mt-2">
                Find this QR anytime in Settings → Public Waitlist Link
              </p>
            </>
          ) : (
            <button
              onClick={handleDone}
              className="w-full py-3.5 bg-primary text-white font-bold text-sm rounded-2xl active:opacity-90"
            >
              Start Managing Your Queue →
            </button>
          )}
        </div>
      </div>

    </div>
  );
}

export default Welcome;
